import { useTranslation } from "react-i18next"
import { useStore } from "../../../contexts/store"
import { ToggleButton, ToggleButtonGroup, Paper, Box, Popover, Slider, IconButton, MenuItem, ListItemIcon, ListItemText, Menu } from "@mui/material"
import FreehandIcon from '@mui/icons-material/FormatShapes'
import { observer } from "mobx-react-lite"
import { JSX, useState } from "react"
import { HighlighterIcon } from "../../Icons/HighlighterIcon"
import { PencilIcon } from "../../Icons/PencilIcon"
import ColorPicker from "../../Elements/ColorPicker"
import { Add, Close, DeleteForever, EditNote, MoreHoriz, Settings } from "@mui/icons-material"
import { SelectionTool } from "../../../shared/src/types/Session"
import { Op_editUserData } from "../../../shared/src/types/Ops"
import Clicktip from "../../Elements/Clicktip"

export const toolIcons = {
  'marker': <EditNote />,
  'box': <FreehandIcon fontSize="small" />,
  'pencil': <PencilIcon style={{width:20, height:20}} />,
  'highlighter': <HighlighterIcon style={{width:20, height:20}} />
}

type toolPopoverInfo = {
  element:HTMLElement,
  toolNumber: number,
  color: string,
  radius?: number,
}

const ToolCustomizer = observer(({tool, setTool, popoverClose}: {tool:SelectionTool|null, setTool:any, popoverClose:any}) => {
    const { sessionStore } = useStore()
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    if (!tool) return null

    const setColor = (color:string) => {
      setTool({
        type: tool.type,
        color:color,
        radius: tool.radius
      })
    }

    const setRadius = (radius:number) => {
      setTool({
        type: tool.type,
        color: tool.color,
        radius: radius
      })
    }

    const style = {
      p:'10px',
      width:82,
      position:'relative',
      marginBottom:'8px',
      textAlign:'center',
      '&:before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        bottom: 0,
        left: 25,
        width: 10,
        height: 10,
        backgroundColor: 'inherit',
        transform: 'translateY(50%) rotate(45deg)',
        boxShadow: '3px 3px 5px 2px rgba(0,0,0,0.1)',
      }
    }

    const radius = (tool.type === 'pencil' || tool.type === 'highlighter') ? <div style={{position:'relative'}}>
      <span style={{color:'#888', fontSize:10, transform:'rotate(270deg)', position:'absolute', left:'-20px', top:'54px', width:'42px', display:'block'}}>{t('Radius')}</span>
      <Slider
        aria-label={t('Drag to change the radius')||''}
        title={t('Drag to change the radius')||''}
        min={2} max={40} value={tool.radius}
        sx={{height:100, mt:2, ml:'0px'}}
        orientation='vertical'
        valueLabelDisplay="auto"
        onChange={(e:Event, v:number|number[]) => {setRadius(v as number)}}
      />
    </div> : <div style={{height:'4px'}}></div>

    const colorPickerCircle = (tool.type === 'pencil' || tool.type === 'highlighter') ? <div style={{ height:42, width:42, position:'relative' }}>
      <div style={{position:'absolute', top:`${21-(tool.radius||2)/2}px`, left: `${21-(tool.radius||2)/2}px`, backgroundColor:tool.color, width:tool.radius+'px', height: tool.radius+'px', border:'1px solid #888', borderRadius:100}}></div>
    </div> : null

    const deleteButtonDisabled = sessionStore.session?.user.toolset && sessionStore.session.user.toolset.filter(t => t.type === tool.type).length === 1
    const addButtonDisabled = (sessionStore.session?.user.toolset && sessionStore.session.user.toolset?.length>=7)

    const menu = <Menu open={Boolean(anchorEl)} onClose={() => { setAnchorEl(null) }} anchorEl={anchorEl}>
      <MenuItem onClick={() => { popoverClose() }}>
        <ListItemIcon>
          <Close fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('Close without saving')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => { popoverClose(-1, tool) }} disabled={addButtonDisabled}>
        <ListItemIcon>
          <Add fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('Add as new tool')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => { popoverClose(-2, tool) }} disabled={deleteButtonDisabled}>
        <ListItemIcon>
          <DeleteForever fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('Delete this tool')}</ListItemText>
    </MenuItem>
    </Menu>

    return <div style={{padding:'0 30px 0 30px'}} onClick={(e:any) => {  popoverClose(sessionStore.selectionToolNumber, tool) }}>
      <Paper elevation={3} sx={style} onClick={(e) => {e.stopPropagation()}}>
        <Box sx={{mb:.5, height:'40px'}}>
          {toolIcons[tool.type]}
        </Box>

        <span style={{color:'#888', fontSize:10, transform:'rotate(270deg)', position:'absolute', left:'-10px', top:'66px', width:'42px', display:'block'}}>{t('Color')}</span>
        <Box sx={{border:'1px solid ' + tool.color, boxShadow:'0px 0px 2px 0px ' + tool.color, marginLeft:'10px', width:42, height:42, borderRadius:'4px', '&:hover': { cursor:'pointer', boxShadow:'0px 0px 4px 0px ' + tool.color }}} title={t('Click to change the color') || ''}>
          <ColorPicker color={tool.color} setColor={setColor} sx={{width:40, height:40}}>{colorPickerCircle}</ColorPicker>
        </Box>

        {radius}

        <span style={{color:'#888', fontSize:10, transform:'rotate(270deg)', position:'absolute', left:'-10px', bottom:'26px', width:'42px', display:'block'}}>{t('More')}</span>
        <IconButton sx={{mt:'8px', mb:'4px'}} title={t('More options')||''} size='small' aria-label={t('More options')||''} onClick={(e:React.MouseEvent<HTMLButtonElement>) => { setAnchorEl(e.currentTarget) }}>
          <MoreHoriz />
        </IconButton>

      </Paper>
      {menu}
    </div>

})

const Toolbox = observer(({showToolCustomizer, variant = 'auto'}: {showToolCustomizer:any, variant?:'auto'|'button'|'toolset'}) => {
  const { sessionStore, uiStore } = useStore()
  const { t } = useTranslation()
  const currentTool = sessionStore.getSelectionTool()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  if (variant === 'auto') {
    if ((uiStore.deviceWidth === 'smaller') || (uiStore.deviceWidth === 'tiny')) variant = 'button'
  }

  //podStore.pod?.tools || []
  const handleToolchange = (event: React.MouseEvent<HTMLElement>, newTool:number|null) => {
    if (newTool !== null) sessionStore.setSelectionToolNumber(newTool)
    event.stopPropagation()
    event.preventDefault()
  }

  const handleClick = (e:any, selectionToolNumber: number) => {
    showToolCustomizer(e, selectionToolNumber)
  }


  const availableTools:JSX.Element[] = sessionStore.session?.user.toolset?.map((tool, i) => {
    return <ToolButton onClick={handleClick} key={i} value={i} title={t('toolname-' + tool.type) + `\n` + t('(Click to select, click again to modify this tool)')} icon={toolIcons[tool.type]} />
  }) || []

  if (variant === 'button' && (currentTool)) return <>
    <ToggleButton
      value={1}
      sx={{width:40, height:40}}
      title={t('toolname-' + currentTool.type) || ''}
      onClick={(e:React.MouseEvent<HTMLElement>) => { setAnchorEl(e.currentTarget) }}
    >
      {toolIcons[currentTool.type]}
      <span style={{backgroundColor:currentTool.color, width:8, height:8, position:'absolute', right:2, bottom:2, borderRadius:20, zIndex:0, boxShadow: '0px 0px 1px 1px #000'}}></span>
    </ToggleButton>
    <Popover
          id={'id'}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => { setAnchorEl(null) }}
          slotProps={{
            paper: { style: { padding: '5px' } },
            backdrop: { sx: { background:'#0008' } }
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
        <ToggleButtonGroup
          value={sessionStore.selectionToolNumber}
          exclusive
          onChange={handleToolchange}
          aria-label={t('Select a tool')||''}
          title={t('Select a tool')||''}
          size="small"
        >
          {availableTools}
      </ToggleButtonGroup>
    </Popover>
  </>

  return <Clicktip variant="tooltip" id="toolSelection" modal><ToggleButtonGroup
      value={sessionStore.selectionToolNumber}
      exclusive
      onChange={handleToolchange}
      aria-label={t('Select a tool')||''}
      title={t('Select a tool')||''}
      size="small"    >
      {availableTools}
  </ToggleButtonGroup>
  </Clicktip>
})

const ToolButton = observer(({value, title, icon, onClick}: { value:number, title:string, icon:any, onClick:any }) => {
  const { sessionStore } = useStore()
  if (!sessionStore.session?.user.toolset) return null
  const tool = sessionStore.session.user.toolset[value]
  if (!tool) return null

  const gear = (value === sessionStore.selectionToolNumber) ? <Settings sx={{
    position:'absolute',
    fontSize:'12px',
    top:0,
    right:0,
  }} /> : null

  return <ToggleButton
    sx={{width:40, height:40}}
    key={value}
    value={value}
    title={title}
    onClick={(e) => {
      if (sessionStore.selectionToolNumber === value) { onClick(e.currentTarget, value) }
    }}
  >
    {icon}
    <span style={{backgroundColor:tool.color, width:8, height:8, position:'absolute', right:2, bottom:2, borderRadius:20, zIndex:0, boxShadow: '0px 0px 1px 1px #000'}}></span>
    {gear}
  </ToggleButton>
})


const ToolSelection = ({variant = 'auto'}: {variant?:'auto'|'button'|'toolset'}) => {
  const { sessionStore, opStore, podStore, uiStore } = useStore()
  const [popoverTrigger, setPopoverTrigger] = useState<toolPopoverInfo|null>(null)
  const [currentTool, setCurrentTool] = useState<SelectionTool|null>(null)
  var operationSent = false

  if (variant === 'auto') {
    if ((uiStore.deviceWidth === 'smaller') || (uiStore.deviceWidth === 'tiny')) variant = 'button'
  }

  const popoverOpen = (e:HTMLElement, selectionToolNumber:number) => {
    const tool = sessionStore.session?.user.toolset && sessionStore.session.user.toolset[selectionToolNumber]
    if (tool) {
      setPopoverTrigger({
        element: e,
        toolNumber: selectionToolNumber,
        color: tool.color,
        radius: tool.radius,
      })
      setCurrentTool(JSON.parse(JSON.stringify(tool)))
    }
  }

  const popoverClose = (id?:number, tool?:SelectionTool) => {

    if (sessionStore.session?.user.toolset) {
      const tools = JSON.parse(JSON.stringify(sessionStore.session?.user.toolset))

      if ((typeof id === 'number') && (typeof tool === 'object')) {
        if (id === -1) {
          tools.splice(sessionStore.selectionToolNumber+1, 0, tool)
          sessionStore.setSelectionToolNumber(sessionStore.selectionToolNumber+1)
          setCurrentTool(sessionStore.getSelectionTool())
        }
        else if (id === -2) {
          tools.splice(sessionStore.selectionToolNumber, 1)
          sessionStore.setSelectionToolNumber(sessionStore.selectionToolNumber-1)
          setCurrentTool(sessionStore.getSelectionTool())
        }
        else {
          tools[id] = tool
        }

        const op:Op_editUserData = {
          op:'editUserData',
          podId: null,
          data: {
            userId: sessionStore.session.user.userId,
            userName: null,
            usergroupId: '*private',
            mods: {
              toolset: tools,
            }
          }
        }

        if (!operationSent) opStore.doOp(op)
        operationSent = true
      }
    }

    setPopoverTrigger(null)
    setCurrentTool(null)
  }

  document.getElementById('root')?.parentElement?.classList.remove("use-freehand-marker")
  document.getElementById('root')?.parentElement?.classList.remove("use-freehand-box")
  document.getElementById('root')?.parentElement?.classList.remove("use-freehand-pencil")
  document.getElementById('root')?.parentElement?.classList.remove("use-freehand-highlighter")
  document.getElementById('root')?.parentElement?.classList.add("use-freehand-" + sessionStore.getSelectionTool()?.type)

  return (<>
    <Toolbox showToolCustomizer={popoverOpen} variant={variant} />
    <Popover
          id={'id'}
          open={Boolean(popoverTrigger !== null)}
          anchorEl={popoverTrigger?.element}
          onClose={() => { if (currentTool) popoverClose(sessionStore.selectionToolNumber, currentTool) }}
          slotProps={{
            paper: {
              style: { background:'none', boxShadow:'none', padding: '5px' }
            },
            backdrop: { sx: { background:'#0008' }}
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
      <ToolCustomizer tool={currentTool} setTool={setCurrentTool} popoverClose={popoverClose} />
    </Popover>
  </>
  )
}

export default observer(ToolSelection)